import {
  equippedPockets, getItemDefinition, lightBatteryFraction, pocketAccessSeconds, pocketVolumeMl, samePocket,
  type ClientPhysicalAction, type EquippedPocket, type ItemStack, type PocketRef,
} from "@endclock/shared";
import type { NetworkClient } from "../game/network.js";
import type { ClientInventorySnapshot, ClientInventoryStore } from "./inventory-store.js";
import { carryIcon } from "./carry-icons.js";
import { itemIcon } from "./item-icons.js";

export type CarrySelection = { area: "PLAYER" | "CONTAINER"; stackId: string } | null;
type Action = Omit<ClientPhysicalAction, "t" | "requestId" | "expectedRevision">;
const MAX_QUICK_POCKETS = 8;

export class PhysicalCarryUi {
  private readonly quickBar = document.querySelector<HTMLElement>("#carry-quick-bar")!;
  private readonly handButton = document.querySelector<HTMLButtonElement>("#carry-hand")!;
  private readonly progress = document.querySelector<HTMLElement>("#carry-progress")!;
  private readonly progressLabel = this.progress.querySelector("span")!;
  private readonly progressBar = this.progress.querySelector("progress")!;
  private readonly feedback = document.querySelector<HTMLElement>("#carry-feedback")!;
  private sequence = 0;
  private readonly keyHandler = (event: KeyboardEvent) => {
    const crouchShortcut = event.ctrlKey && this.ownsPointerAim() && (event.code === "KeyF" || /^Digit[1-5]$/.test(event.code));
    if (!this.gameplayActionsEnabled() || event.repeat || event.ctrlKey && !crouchShortcut || event.altKey || event.metaKey || isTextEntry(event.target)) return;
    const index = /^Digit([1-8])$/.exec(event.code)?.[1];
    const pocket = index ? this.quickPockets()[Number(index) - 1] : undefined;
    if (pocket) { event.preventDefault(); this.act({ action: "QUICK", pocket: pocket.ref }); }
    if (event.code === "KeyF" && this.store.snapshot().physical?.hands.MAIN_HAND?.item.device) {
      event.preventDefault(); this.act({ action: "TOGGLE_LIGHT" });
    }
  };
  private readonly timer: number;

  constructor(
    private readonly store: ClientInventoryStore,
    private readonly network: NetworkClient,
    private readonly selection: () => CarrySelection,
    private readonly select: (id: string) => void,
    private readonly takeFromContainer: (destination: PocketRef) => void,
    private readonly gameplayActionsEnabled: () => boolean = () => true,
    private readonly ownsPointerAim: () => boolean = () => false,
  ) {
    this.handButton.addEventListener("click", () => {
      if (!this.gameplayActionsEnabled()) return;
      const hand = this.store.snapshot().physical?.hands.MAIN_HAND;
      if (hand?.item.device) this.act({ action: "TOGGLE_LIGHT" });
      else if (hand) this.act({ action: "STOW" });
    });
    document.querySelector("#carry-cancel")!.addEventListener("click", () => this.act({ action: "CANCEL" }));
    window.addEventListener("keydown", this.keyHandler);
    this.timer = window.setInterval(() => this.renderProgress(), 100);
  }

  destroy(): void { window.clearInterval(this.timer); window.removeEventListener("keydown", this.keyHandler); }

  act(action: Action): void {
    const snapshot = this.store.snapshot();
    if (!snapshot.physical || this.network.state !== "online") return;
    const requestId = `carry_${this.network.sessionId.slice(-24)}_${(++this.sequence).toString(36)}`;
    if (!this.store.markPhysicalPending(requestId)) return;
    this.network.reportActivity("interaction");
    this.network.physicalAction({ t: "physical_action", requestId, expectedRevision: snapshot.physical.revision, ...action });
  }

  render(snapshot: ClientInventorySnapshot, grid: HTMLElement): void {
    const state = snapshot.physical;
    grid.classList.add("physical-pocket-grid");
    grid.replaceChildren();
    const character = document.querySelector<HTMLElement>("#carry-character")!;
    character.replaceChildren();
    if (!state) { character.textContent = "SYNCING EQUIPMENT…"; this.quickBar.replaceChildren(); return; }
    const body = document.createElement("div");
    body.className = "carry-silhouette";
    body.innerHTML = carryIcon("character");
    character.append(body);
    const equipment = document.createElement("div");
    equipment.className = "carry-equipment";
    const title = document.createElement("small"); title.textContent = "EQUIPPED"; equipment.append(title);
    for (const [slot, item] of Object.entries(state.equipment)) {
      const line = document.createElement("strong");
      line.textContent = `${slot.replaceAll("_", " ")} — ${getItemDefinition(item.definitionId)?.name ?? item.definitionId}`;
      equipment.append(line);
    }
    const hand = state.hands.MAIN_HAND;
    const handTarget = button(`MAIN HAND — ${hand ? itemName(hand.item) : "EMPTY"}`, "carry-hand-target");
    handTarget.addEventListener("click", () => {
      const selection = this.selection();
      if (selection?.area === "PLAYER" && selection.stackId !== hand?.item.instanceId) this.act({ action: "RETRIEVE", itemInstanceId: selection.stackId });
      else if (hand) this.select(hand.item.instanceId);
    });
    equipment.append(handTarget);
    character.append(equipment);
    for (const pocket of equippedPockets(state)) grid.append(this.pocketCard(pocket));
    this.renderQuickBar(snapshot);
    this.renderHand(snapshot);
    this.renderProgress();
    const result = snapshot.physicalResult;
    this.feedback.textContent = result && result.result !== "OK" ? result.result.replaceAll("_", " ") : "";
  }

  private pocketCard(pocket: EquippedPocket): HTMLElement {
    const state = this.store.snapshot().physical!;
    const card = document.createElement("section");
    card.className = "physical-pocket";
    card.dataset.pocket = pocket.ref.pocketId;
    const used = pocketVolumeMl(pocket.state);
    const header = button(pocket.definition.name, "physical-pocket-destination");
    header.title = "Tap an item, then this pocket to move it here";
    header.addEventListener("click", () => {
      const selection = this.selection();
      if (!selection) return;
      if (selection.area === "CONTAINER") this.takeFromContainer(pocket.ref);
      else if (!pocket.state.items.some(item => item.instanceId === selection.stackId)) {
        this.act({ action: "MOVE", itemInstanceId: selection.stackId, destination: pocket.ref });
      }
    });
    card.append(header);
    const detail = document.createElement("span"); detail.className = "physical-pocket-capacity";
    detail.textContent = `${Math.round(used)} / ${pocket.definition.capacityMl} ml · ${used / pocket.definition.capacityMl > .75 ? "CROWDED" : `${pocket.definition.baseAccessSeconds.toFixed(1)} s`}`;
    card.append(detail);
    const meter = document.createElement("meter"); meter.max = pocket.definition.capacityMl; meter.value = used;
    meter.setAttribute("aria-label", `${pocket.definition.name} capacity`); card.append(meter);
    const items = document.createElement("div"); items.className = "physical-pocket-items";
    for (const item of pocket.state.items) {
      const itemButton = button("", "inventory-slot physical-item");
      itemButton.dataset.occupied = "true";
      itemButton.dataset.area = "PLAYER";
      itemButton.dataset.instance = item.instanceId;
      itemButton.dataset.selected = String(this.selection()?.stackId === item.instanceId);
      const seconds = pocketAccessSeconds(state, pocket.ref, item.instanceId);
      itemButton.setAttribute("aria-label", `${itemName(item)}, ${seconds.toFixed(1)} seconds${pocket.state.quickItemId === item.instanceId ? ", quick item" : ""}`);
      itemButton.innerHTML = `<span class="physical-item-icon">${itemArtwork(item)}</span><span class="physical-item-label"></span><small></small>`;
      itemButton.querySelector(".physical-item-label")!.textContent = `${itemName(item)}${item.quantity > 1 ? ` ×${item.quantity}` : ""}`;
      itemButton.querySelector("small")!.textContent = `${pocket.state.quickItemId === item.instanceId ? "QUICK · " : ""}${seconds.toFixed(1)} s`;
      itemButton.addEventListener("click", () => this.select(item.instanceId));
      items.append(itemButton);
    }
    if (!pocket.state.items.length) {
      const empty = document.createElement("span"); empty.className = "physical-pocket-empty"; empty.textContent = "EMPTY"; items.append(empty);
    }
    card.append(items);
    return card;
  }

  appendDetails(root: HTMLElement, item: ItemStack | null, snapshot: ClientInventorySnapshot): void {
    if (!item || !snapshot.physical || this.selection()?.area !== "PLAYER") return;
    const state = snapshot.physical;
    const pocket = equippedPockets(state).find(p => p.state.items.some(value => value.instanceId === item.instanceId));
    const held = state.hands.MAIN_HAND?.item.instanceId === item.instanceId;
    const actions = document.createElement("div"); actions.className = "carry-detail-actions";
    const add = (label: string, action: Action, disabled = false) => {
      const control = button(label); control.disabled = disabled || !!snapshot.physicalPending || !!state.action;
      control.addEventListener("click", () => this.act(action)); actions.append(control);
    };
    if (pocket) {
      add(`DRAW · ${pocketAccessSeconds(state, pocket.ref, item.instanceId).toFixed(1)} s`, { action: "RETRIEVE", itemInstanceId: item.instanceId, pocket: pocket.ref });
      add(pocket.state.quickItemId === item.instanceId ? "CLEAR QUICK ITEM" : "SET QUICK ITEM", { action: "BIND", pocket: pocket.ref, ...(pocket.state.quickItemId === item.instanceId ? {} : { itemInstanceId: item.instanceId }) });
    }
    if (held) {
      add("STOW", { action: "STOW" });
      if (item.device) add(item.device.on ? "LIGHT OFF" : "LIGHT ON", { action: "TOGGLE_LIGHT" });
    }
    if (item.battery) {
      const charge = document.createElement("span"); charge.textContent = `CELL ${Math.round(item.battery.remainingWh / item.battery.capacityWh * 100)}% · ${item.battery.remainingWh.toFixed(2)} Wh`; root.append(charge);
    }
    if (item.device) {
      const bay = document.createElement("div"); bay.className = "carry-battery-bay";
      if (!held) { const hint = document.createElement("span"); hint.textContent = "Hold the flashlight to replace its batteries."; bay.append(hint); }
      const available = equippedPockets(state).flatMap(p => p.state.items).filter(value => !!value.battery);
      for (let slot = 0; slot < item.device.cells.length; slot++) {
        const cell = item.device.cells[slot];
        const row = document.createElement("div"); row.className = "carry-battery-row";
        const label = document.createElement("span"); label.textContent = `AA SLOT ${slot + 1} — ${cell?.battery ? `${Math.round(cell.battery.remainingWh / cell.battery.capacityWh * 100)}%` : "EMPTY"}`; row.append(label);
        if (cell) {
          const remove = button("REMOVE"); remove.disabled = !held || !!state.action || !!snapshot.physicalPending;
          remove.addEventListener("click", () => this.act({ action: "REMOVE_BATTERY", itemInstanceId: item.instanceId, cellSlot: slot })); row.append(remove);
        } else {
          const select = document.createElement("select"); select.setAttribute("aria-label", `Battery for AA slot ${slot + 1}`);
          for (const battery of available) {
            const option = document.createElement("option"); option.value = battery.instanceId;
            option.textContent = `${itemName(battery)} ${Math.round(battery.battery!.remainingWh / battery.battery!.capacityWh * 100)}%`; select.append(option);
          }
          select.disabled = !held || !available.length; row.append(select);
          const insert = button("INSERT"); insert.disabled = !held || !available.length || !!state.action || !!snapshot.physicalPending;
          insert.addEventListener("click", () => this.act({ action: "INSERT_BATTERY", itemInstanceId: item.instanceId, cellSlot: slot, batteryInstanceId: select.value })); row.append(insert);
        }
        bay.append(row);
      }
      root.append(bay);
    }
    root.append(actions);
  }

  private quickPockets(): readonly EquippedPocket[] {
    const state = this.store.snapshot().physical;
    return state ? equippedPockets(state).filter(p => p.definition.quickAccessEligible).slice(0, MAX_QUICK_POCKETS) : [];
  }

  private renderQuickBar(snapshot: ClientInventorySnapshot): void {
    this.quickBar.replaceChildren();
    for (const [index, pocket] of this.quickPockets().entries()) {
      const hand = snapshot.physical?.hands.MAIN_HAND;
      const item = pocket.state.items.find(item => item.instanceId === pocket.state.quickItemId)
        ?? (samePocket(hand?.origin ?? null, pocket.ref) ? hand?.item : undefined);
      const control = button("", "carry-quick-slot");
      control.dataset.pocket = pocket.ref.pocketId;
      control.dataset.held = String(item?.instanceId === hand?.item.instanceId && !!item);
      control.setAttribute("aria-label", `${index + 1}: ${pocket.definition.name}${item ? `, ${itemName(item)}` : ", no quick item"}`);
      control.title = control.getAttribute("aria-label")!;
      control.innerHTML = `${item ? itemArtwork(item) : carryIcon("pocket")}<small>${index + 1}</small>`;
      control.disabled = !item || !!snapshot.physicalPending || !!snapshot.physical?.action;
      control.addEventListener("click", () => { if (this.gameplayActionsEnabled()) this.act({ action: "QUICK", pocket: pocket.ref }); });
      this.quickBar.append(control);
    }
  }

  private renderHand(snapshot: ClientInventorySnapshot): void {
    const item = snapshot.physical?.hands.MAIN_HAND?.item;
    const name = item ? itemName(item) : "EMPTY HAND";
    const charge = item?.device ? `${Math.round(lightBatteryFraction(item) * 100)}%` : "";
    const on = !!item?.device?.on;
    this.handButton.dataset.on = String(on);
    this.handButton.innerHTML = `${item ? itemArtwork(item) : carryIcon("hand")}<span></span>`;
    this.handButton.querySelector("span")!.textContent = item?.device ? `${on ? "ON" : "OFF"} ${charge}` : name;
    this.handButton.setAttribute("aria-label", `${name}${item?.device ? `, light ${on ? "on" : "off"}, battery ${charge}. Toggle light (F)` : ""}`);
    this.handButton.disabled = !item || !!snapshot.physicalPending || !!snapshot.physical?.action;
  }

  private renderProgress(): void {
    const snapshot = this.store.snapshot();
    const action = snapshot.physical?.action;
    this.progress.hidden = !action;
    if (!action) return;
    const at = (snapshot.physicalAt ?? 0) + Math.max(0, performance.now() - (snapshot.physicalReceivedAt ?? performance.now()));
    const seconds = Math.max(0, (action.completesAt - at) / 1_000);
    const ref = typeof action.source === "object" ? action.source : action.destination;
    const name = typeof ref === "object" ? equippedPockets(snapshot.physical!).find(p => samePocket(p.ref, ref))?.definition.name : "MAIN HAND";
    const verb = action.type === "RETRIEVE" ? "SEARCHING" : action.type === "INSERT_BATTERY" ? "INSERTING CELL" : action.type === "REMOVE_BATTERY" ? "REMOVING CELL" : "STOWING";
    this.progressLabel.textContent = `${name ?? "POCKET"} · ${verb}… ${seconds.toFixed(1)} s`;
    this.progressBar.value = Math.max(0, Math.min(.99, (at - action.startedAt) / Math.max(1, action.completesAt - action.startedAt)));
  }
}

function itemName(item: ItemStack): string { return getItemDefinition(item.definitionId)?.name ?? item.definitionId; }
function itemArtwork(item: ItemStack): string {
  const definition = getItemDefinition(item.definitionId);
  if (definition?.lightDevice) return carryIcon("flashlight");
  if (definition?.battery) return carryIcon("battery");
  if (definition?.wearable) return carryIcon("jeans");
  return `<span aria-hidden="true">${itemIcon(definition)}</span>`;
}
function button(label: string, className = ""): HTMLButtonElement {
  const value = document.createElement("button"); value.type = "button"; value.textContent = label; value.className = className; return value;
}
export function isTextEntry(target: EventTarget | null): boolean {
  return target instanceof Element && !!target.closest("input, textarea, select, [contenteditable]:not([contenteditable='false'])");
}
